import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
  Chip,
  Avatar,
  useTheme,
  alpha,
  Fade,
  Grow,
  Tooltip,
  Badge,
  IconButton,
  Paper
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon
} from '@mui/icons-material';

interface EnhancedSidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  navItems: any[];
  inventoryNavItems: any[];
  financialNavItems: any[];
  settingsNavItems: any[];
  currentUser: any;
  hasPermission: (user: any, permission: string) => boolean;
  onItemClick?: () => void;
}

interface AnimatedNavItemProps {
  item: any;
  collapsed: boolean;
  selected: boolean;
  onClick: () => void;
  level?: number;
  delay?: number;
  index?: number;
}

const AnimatedNavItem: React.FC<AnimatedNavItemProps> = ({
  item,
  collapsed,
  selected,
  onClick,
  level = 0,
  delay = 0,
  index = 0
}) => {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);

  const itemContent = (
    <ListItemButton
      onClick={onClick}
      selected={selected}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        borderRadius: 3,
        mx: collapsed ? 0.5 : 1,
        my: 0.5,
        px: collapsed ? 1 : 2,
        py: 1.5,
        minHeight: 52,
        position: 'relative',
        overflow: 'hidden',
        background: selected
          ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
          : 'transparent',
        border: selected
          ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          : `1px solid transparent`,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: hovered ? 'translateX(-4px) scale(1.02)' : 'translateX(0) scale(1)',
        boxShadow: selected
          ? `0 4px 20px ${alpha(theme.palette.primary.main, 0.25)}`
          : hovered
            ? `0 6px 25px ${alpha(theme.palette.primary.main, 0.15)}`
            : 'none',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          opacity: hovered ? 1 : 0,
          transition: 'opacity 0.3s ease',
          zIndex: 0
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 4,
          height: selected ? '70%' : '0%',
          backgroundColor: theme.palette.primary.main,
          borderRadius: '0 4px 4px 0',
          transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 1
        },
        pl: level > 0 ? (collapsed ? 1 : 4) : (collapsed ? 1 : 2),
        zIndex: 2
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: collapsed ? 0 : 40,
          mr: collapsed ? 0 : 2,
          color: selected ? theme.palette.primary.main : 'inherit',
          transition: 'all 0.4s ease',
          transform: hovered ? 'scale(1.1) rotate(5deg)' : 'scale(1) rotate(0deg)',
          zIndex: 3,
          '& .MuiSvgIcon-root': {
            fontSize: collapsed ? '1.5rem' : '1.3rem',
            filter: selected ? `drop-shadow(0 2px 4px ${alpha(theme.palette.primary.main, 0.3)})` : 'none'
          }
        }}
      >
        {item.badge ? (
          <Badge
            badgeContent={item.badge}
            color="error"
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                right: -2,
                top: 2,
                animation: 'pulse 2s infinite'
              }
            }}
          >
            {item.icon}
          </Badge>
        ) : (
          item.icon
        )}
      </ListItemIcon>

      {!collapsed && (
        <ListItemText
          primary={item.name}
          sx={{
            zIndex: 3,
            '& .MuiListItemText-primary': {
              fontWeight: selected ? 700 : 500,
              fontSize: '0.875rem',
              color: selected ? theme.palette.primary.main : 'inherit',
              transition: 'all 0.3s ease',
              textShadow: selected ? `0 1px 2px ${alpha(theme.palette.primary.main, 0.2)}` : 'none'
            }
          }}
        />
      )}

      {!collapsed && item.chip && (
        <Chip
          label={item.chip}
          size="small"
          color="primary"
          variant={selected ? "filled" : "outlined"}
          sx={{
            height: 22,
            fontSize: '0.75rem',
            ml: 1,
            zIndex: 3,
            transition: 'all 0.3s ease',
            transform: hovered ? 'scale(1.05)' : 'scale(1)'
          }}
        />
      )}
    </ListItemButton>
  );

  if (collapsed && item.name) {
    return (
      <Tooltip
        title={item.name}
        placement="left"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontSize: '0.875rem',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }
        }}
      >
        <Box>{itemContent}</Box>
      </Tooltip>
    );
  }

  return (
    <Grow in timeout={600 + delay}>
      <Box>{itemContent}</Box>
    </Grow>
  );
};

interface EnhancedSectionProps {
  title: string;
  icon: React.ReactNode;
  items: any[];
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
  currentPath: string;
  onItemClick: (path: string) => void;
  color?: string;
  delay?: number;
}

const EnhancedSection: React.FC<EnhancedSectionProps> = ({
  title,
  icon,
  items,
  collapsed,
  open,
  onToggle,
  currentPath,
  onItemClick,
  color = 'primary',
  delay = 0
}) => {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);
  const sectionColor = (theme.palette[color as keyof typeof theme.palette] as any)?.main || theme.palette.primary.main;
  const hasActiveItem = items.some(item => item.path === currentPath);

  return (
    <Grow in timeout={800 + delay}>
      <Box sx={{ mb: 1.5 }}>
        {/* Section Header */}
        <Paper
          elevation={hasActiveItem ? 4 : hovered ? 2 : 0}
          sx={{
            borderRadius: 3,
            mx: collapsed ? 0.5 : 1,
            overflow: 'hidden',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            background: hasActiveItem
              ? `linear-gradient(135deg, ${alpha(sectionColor, 0.15)} 0%, ${alpha(sectionColor, 0.08)} 100%)`
              : `linear-gradient(135deg, ${alpha(sectionColor, 0.08)} 0%, ${alpha(sectionColor, 0.04)} 100%)`,
            border: `1px solid ${alpha(sectionColor, hasActiveItem ? 0.3 : 0.12)}`,
            transform: hovered ? 'translateX(-2px)' : 'translateX(0)'
          }}
        >
          <ListItemButton
            onClick={onToggle}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{
              px: collapsed ? 1 : 2,
              py: 1.5,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: alpha(sectionColor, 0.08)
              }
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: collapsed ? 0 : 40,
                mr: collapsed ? 0 : 2,
                color: sectionColor,
                transition: 'all 0.3s ease',
                transform: hovered ? 'scale(1.1)' : 'scale(1)',
                '& .MuiSvgIcon-root': {
                  filter: `drop-shadow(0 2px 4px ${alpha(sectionColor, 0.3)})`
                }
              }}
            >
              {icon}
            </ListItemIcon>

            {!collapsed && (
              <>
                <ListItemText
                  primary={title}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      color: sectionColor,
                      textShadow: `0 1px 2px ${alpha(sectionColor, 0.2)}`
                    }
                  }}
                />
                <Box
                  sx={{
                    transition: 'transform 0.3s ease',
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    color: sectionColor
                  }}
                >
                  <ExpandMoreIcon />
                </Box>
              </>
            )}
          </ListItemButton>
        </Paper>

        {/* Section Items */}
        <Collapse in={!collapsed && open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ mt: 0.5 }}>
            {items.map((item, index) => (
              <Fade in timeout={400 + index * 100} key={item.name}>
                <Box>
                  <AnimatedNavItem
                    item={item}
                    collapsed={false}
                    selected={currentPath === item.path}
                    onClick={() => onItemClick(item.path)}
                    level={1}
                    delay={index * 100}
                    index={index}
                  />
                </Box>
              </Fade>
            ))}
          </List>
        </Collapse>

        {/* Collapsed Section Items */}
        {collapsed && items.map((item, index) => (
          <Fade in timeout={400 + index * 100} key={item.name}>
            <Box>
              <AnimatedNavItem
                item={item}
                collapsed={true}
                selected={currentPath === item.path}
                onClick={() => onItemClick(item.path)}
                delay={index * 100}
                index={index}
              />
            </Box>
          </Fade>
        ))}
      </Box>
    </Grow>
  );
};

const EnhancedSidebar: React.FC<EnhancedSidebarProps> = ({
  collapsed,
  onToggleCollapse,
  navItems,
  inventoryNavItems,
  financialNavItems,
  settingsNavItems,
  currentUser,
  hasPermission,
  onItemClick
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [financialOpen, setFinancialOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleItemClick = (path: string) => {
    navigate(path);
    onItemClick?.();
  };

  // Auto-expand sections based on current path
  useEffect(() => {
    if (inventoryNavItems.some(item => item.path === location.pathname)) {
      setInventoryOpen(true);
    }
    if (financialNavItems.some(item => item.path === location.pathname)) {
      setFinancialOpen(true);
    }
    if (settingsNavItems.some(item => item.path === location.pathname)) {
      setSettingsOpen(true);
    }
  }, [location.pathname, inventoryNavItems, financialNavItems, settingsNavItems]);

  return (
    <Box
      sx={{
        height: '100%',
        background: `linear-gradient(180deg,
          ${alpha(theme.palette.background.paper, 0.95)} 0%,
          ${alpha(theme.palette.background.paper, 0.98)} 50%,
          ${alpha(theme.palette.background.default, 0.95)} 100%)`,
        backdropFilter: 'blur(20px)',
        borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          zIndex: 1
        }
      }}
    >
      {/* Header */}
      <Fade in timeout={600}>
        <Box sx={{ p: collapsed ? 1 : 2, pb: 1, position: 'relative', zIndex: 2 }}>
          {!collapsed && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: theme.palette.primary.main,
                  mr: 2,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}
              >
                {currentUser?.name?.charAt(0) || 'U'}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" noWrap>
                  {currentUser?.name || 'المستخدم'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {currentUser?.role || 'مدير النظام'}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Toggle Button */}
          <Box sx={{ display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end', mb: 1 }}>
            <IconButton
              onClick={onToggleCollapse}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Box>

          <Divider sx={{ opacity: 0.6 }} />
        </Box>
      </Fade>

      {/* Navigation */}
      <Box
        sx={{
          px: 0.5,
          pb: 2,
          overflowY: 'auto',
          height: collapsed ? 'calc(100% - 80px)' : 'calc(100% - 140px)',
          '&::-webkit-scrollbar': {
            width: 6
          },
          '&::-webkit-scrollbar-track': {
            background: alpha(theme.palette.divider, 0.1)
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(theme.palette.primary.main, 0.3),
            borderRadius: 3
          }
        }}
      >
        <List component="nav" disablePadding>
          {/* Main Navigation Items */}
          {navItems.map((item, index) => (
            hasPermission(currentUser, item.permission) && (
              <AnimatedNavItem
                key={item.name}
                item={item}
                collapsed={collapsed}
                selected={location.pathname === item.path}
                onClick={() => handleItemClick(item.path)}
                delay={index * 100}
                index={index}
              />
            )
          ))}

          {/* Sections */}
          {hasPermission(currentUser, 'canAccessInventory') && (
            <EnhancedSection
              title="إدارة المنتجات"
              icon={<TrendingUpIcon />}
              items={inventoryNavItems}
              collapsed={collapsed}
              open={inventoryOpen}
              onToggle={() => setInventoryOpen(!inventoryOpen)}
              currentPath={location.pathname}
              onItemClick={handleItemClick}
              color="info"
              delay={200}
            />
          )}

          {(hasPermission(currentUser, 'canAccessDebts') || hasPermission(currentUser, 'canViewSales') || hasPermission(currentUser, 'canViewCustomers')) && (
            <EnhancedSection
              title="شؤون مالية"
              icon={<DashboardIcon />}
              items={financialNavItems}
              collapsed={collapsed}
              open={financialOpen}
              onToggle={() => setFinancialOpen(!financialOpen)}
              currentPath={location.pathname}
              onItemClick={handleItemClick}
              color="success"
              delay={300}
            />
          )}

          {hasPermission(currentUser, 'canAccessSettings') && (
            <EnhancedSection
              title="الإعدادات"
              icon={<Brightness4Icon />}
              items={settingsNavItems}
              collapsed={collapsed}
              open={settingsOpen}
              onToggle={() => setSettingsOpen(!settingsOpen)}
              currentPath={location.pathname}
              onItemClick={handleItemClick}
              color="warning"
              delay={400}
            />
          )}
        </List>
      </Box>
    </Box>
  );
};

export default EnhancedSidebar;
